class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => {
      if (evt.code !== 'Escape') return;
      const openDropdown = evt.target.closest?.('[data-te-offer-dropdown][open]');
      if (openDropdown) {
        evt.preventDefault();
        evt.stopPropagation();
        this.closeOfferDropdown(openDropdown, true);
        return;
      }
      this.close();
    });
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.addEventListener('click', this.onUpsellAdd.bind(this));
    this.addEventListener('click', this.onOfferBuilderClick.bind(this));
    this.addEventListener('change', this.onOfferBuilderChange.bind(this));
    this.setHeaderCartIconAccessibility();
    this.initializeOfferBuilder();

    this.offerBuilderObserver = new MutationObserver(() => this.scheduleOfferBuilderInitialization());
    const drawerContent = this.querySelector('#CartDrawer');
    if (drawerContent) this.offerBuilderObserver.observe(drawerContent, { childList: true, subtree: true });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  onUpsellAdd(event) {
    const button = event.target.closest('[data-te-cart-upsell-add]');
    if (!button) return;

    event.preventDefault();
    if (button.disabled || button.getAttribute('aria-disabled') === 'true') return;

    const variantId = Number(button.dataset.variantId);
    if (!variantId) return;

    const errorElement = button.closest('.te-offer-panel, .te-cart-set')?.querySelector('[data-te-cart-upsell-error]');
    if (errorElement) {
      errorElement.hidden = true;
      errorElement.textContent = '';
    }

    button.setAttribute('aria-disabled', true);
    button.disabled = true;
    button.classList.add('is-loading');
    this.setActiveElement(button);
    const activeOfferId = this.getActiveOfferId();

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    config.body = JSON.stringify({
      items: [
        {
          id: variantId,
          quantity: 1,
        },
      ],
      sections: this.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname,
    });

    const cartAddUrl = window.routes?.cart_add_url || '/cart/add.js';

    fetch(cartAddUrl, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          throw new Error(response.description || response.message || window.cartStrings?.error || 'Cart error');
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-drawer-upsell',
          productVariantId: variantId,
          cartData: response,
        });

        this.refreshContents(response, activeOfferId);
      })
      .catch((error) => {
        if (errorElement) {
          errorElement.textContent =
            error.message || window.cartStrings?.error || 'Could not add this item. Please choose it from the product page.';
          errorElement.hidden = false;
        }
      })
      .finally(() => {
        button.classList.remove('is-loading');
        button.removeAttribute('aria-disabled');
        button.disabled = false;
        if (this.classList.contains('is-empty')) this.classList.remove('is-empty');
      });
  }

  onOfferBuilderClick(event) {
    const optionButton = event.target.closest('[data-te-offer-option-value]');
    if (optionButton) {
      event.preventDefault();
      if (optionButton.disabled || optionButton.getAttribute('aria-disabled') === 'true') return;

      const control = optionButton.closest('[data-te-offer-option-control]');
      const select = control?.querySelector('[data-te-offer-option-select]');
      if (!select) return;

      select.value = optionButton.dataset.teOfferOptionValue || '';
      select.dispatchEvent(new Event('change', { bubbles: true }));
      this.closeOfferDropdown(optionButton.closest('[data-te-offer-dropdown]'), true);
      return;
    }

    if (!event.target.closest('[data-te-offer-dropdown]')) this.closeOfferDropdowns();

    const offerTarget = event.target.closest('[data-te-offer-target]');
    if (offerTarget) {
      event.preventDefault();
      const builder = offerTarget.closest('[data-te-offer-builder]');
      this.setActiveOffer(builder, offerTarget.dataset.teOfferTarget, true);
      return;
    }

    const offerDot = event.target.closest('[data-te-offer-dot]');
    if (offerDot) {
      event.preventDefault();
      const builder = offerDot.closest('[data-te-offer-builder]');
      const panels = [...builder.querySelectorAll('[data-te-offer-panel]')];
      const panel = panels[Number(offerDot.dataset.teOfferDot)];
      if (panel) this.setActiveOffer(builder, panel.dataset.offerId, true);
      return;
    }

    const offerPrevious = event.target.closest('[data-te-offer-prev]');
    const offerNext = event.target.closest('[data-te-offer-next]');
    if (offerPrevious || offerNext) {
      event.preventDefault();
      this.moveOffer(offerPrevious || offerNext, offerNext ? 1 : -1);
      return;
    }

    const productPrevious = event.target.closest('[data-te-product-prev]');
    const productNext = event.target.closest('[data-te-product-next]');
    if (productPrevious || productNext) {
      event.preventDefault();
      this.moveProduct(productPrevious || productNext, productNext ? 1 : -1);
      return;
    }

    const productDot = event.target.closest('[data-te-product-dot]');
    if (productDot) {
      event.preventDefault();
      this.setActiveProduct(productDot.closest('[data-te-product-carousel]'), Number(productDot.dataset.teProductDot), true);
      return;
    }

  }

  onOfferBuilderChange(event) {
    const select = event.target.closest('[data-te-offer-option-select]');
    if (!select) return;
    this.updateOfferProduct(select.closest('[data-te-offer-product]'));
  }

  scheduleOfferBuilderInitialization(preferredOfferId = this.getActiveOfferId()) {
    window.clearTimeout(this.offerBuilderInitializationTimer);
    this.offerBuilderInitializationTimer = window.setTimeout(() => this.initializeOfferBuilder(preferredOfferId), 0);
  }

  initializeOfferBuilder(preferredOfferId) {
    this.querySelectorAll('[data-te-offer-builder]').forEach((builder) => {
      if (builder.dataset.teBuilderInitialized === 'true') return;
      builder.dataset.teBuilderInitialized = 'true';

      builder.querySelectorAll('[data-te-offer-product]').forEach((card) => this.initializeOfferProduct(card));

      const offerTrack = builder.querySelector('[data-te-offer-track]');
      if (offerTrack) {
        offerTrack.addEventListener('scroll', () => {
          window.clearTimeout(offerTrack.teOfferScrollTimer);
          offerTrack.teOfferScrollTimer = window.setTimeout(() => this.syncOfferFromScroll(builder), 80);
        }, { passive: true });
      }

      builder.querySelectorAll('[data-te-product-carousel]').forEach((carousel) => {
        const productTrack = carousel.querySelector('[data-te-product-track]');
        if (productTrack) {
          productTrack.addEventListener('scroll', () => {
            window.clearTimeout(productTrack.teProductScrollTimer);
            productTrack.teProductScrollTimer = window.setTimeout(() => this.syncProductFromScroll(carousel), 80);
          }, { passive: true });
        }
        this.setActiveProduct(carousel, 0, false);
      });

      const panels = [...builder.querySelectorAll('[data-te-offer-panel]')];
      const unlockedReward = panels.find((panel) => panel.dataset.offerRewardUnlocked === 'true');
      const selectedId = unlockedReward?.dataset.offerId || (
        panels.some((panel) => panel.dataset.offerId === preferredOfferId)
          ? preferredOfferId
          : builder.dataset.defaultOfferId || panels[0]?.dataset.offerId
      );
      if (selectedId) this.setActiveOffer(builder, selectedId, false);
    });
  }

  getActiveOfferId() {
    return this.querySelector('[data-te-offer-builder]')?.dataset.activeOfferId ||
      this.querySelector('[data-te-offer-panel].is-active')?.dataset.offerId || '';
  }

  setActiveOffer(builder, offerId, shouldScroll) {
    if (!builder || !offerId) return;
    const panels = [...builder.querySelectorAll('[data-te-offer-panel]')];
    const activeIndex = panels.findIndex((panel) => panel.dataset.offerId === offerId);
    if (activeIndex < 0) return;

    builder.dataset.activeOfferId = offerId;
    panels.forEach((panel, index) => {
      const active = index === activeIndex;
      panel.classList.toggle('is-active', active);
      panel.setAttribute('aria-hidden', active ? 'false' : 'true');
    });

    builder.querySelectorAll('[data-te-offer-target]').forEach((button) => {
      const active = button.dataset.teOfferTarget === offerId;
      button.closest('.te-cart-milestone')?.classList.toggle('is-active', active);
      button.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    builder.querySelectorAll('[data-te-offer-dot]').forEach((dot, index) => {
      const active = index === activeIndex;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    const activePanel = panels[activeIndex];
    const helper = builder.querySelector('[data-te-offer-helper]');
    if (helper) helper.textContent = activePanel.dataset.offerHelper || '';
    this.updateOfferArrowState(builder, activeIndex, panels.length);

    const track = builder.querySelector('[data-te-offer-track]');
    if (track) {
      const nextHeight = activePanel.scrollHeight;
      if (nextHeight > 0) track.style.height = `${nextHeight}px`;
    }

    if (!shouldScroll && track && Math.abs(track.scrollLeft - activePanel.offsetLeft) > 2) {
      track.scrollTo({ left: activePanel.offsetLeft, behavior: 'auto' });
    }

    if (shouldScroll && track) {
      track.teTargetOfferId = offerId;
      window.clearTimeout(track.teTargetOfferTimer);
      track.teTargetOfferTimer = window.setTimeout(() => {
        track.teTargetOfferId = '';
      }, 700);
      track.scrollTo({ left: activePanel.offsetLeft, behavior: this.reducedMotion() ? 'auto' : 'smooth' });
    }
  }

  moveOffer(control, direction) {
    const builder = control.closest('[data-te-offer-builder]');
    const panels = [...builder.querySelectorAll('[data-te-offer-panel]')];
    const current = Math.max(0, panels.findIndex((panel) => panel.dataset.offerId === builder.dataset.activeOfferId));
    const next = Math.max(0, Math.min(panels.length - 1, current + direction));
    if (panels[next]) this.setActiveOffer(builder, panels[next].dataset.offerId, true);
  }

  syncOfferFromScroll(builder) {
    const track = builder.querySelector('[data-te-offer-track]');
    const panels = [...builder.querySelectorAll('[data-te-offer-panel]')];
    if (!track || panels.length < 2) return;
    if (track.teTargetOfferId) {
      const targetPanel = panels.find((panel) => panel.dataset.offerId === track.teTargetOfferId);
      if (targetPanel && Math.abs(targetPanel.offsetLeft - track.scrollLeft) > 2) return;
      track.teTargetOfferId = '';
      window.clearTimeout(track.teTargetOfferTimer);
    }
    const closest = panels.reduce((best, panel, index) => {
      const distance = Math.abs(panel.offsetLeft - track.scrollLeft);
      return distance < best.distance ? { index, distance } : best;
    }, { index: 0, distance: Number.POSITIVE_INFINITY });
    this.setActiveOffer(builder, panels[closest.index].dataset.offerId, false);
  }

  updateOfferArrowState(builder, index, count) {
    const previous = builder.querySelector('[data-te-offer-prev]');
    const next = builder.querySelector('[data-te-offer-next]');
    if (previous) previous.disabled = index <= 0;
    if (next) next.disabled = index >= count - 1;
  }

  setActiveProduct(carousel, index, shouldScroll) {
    if (!carousel) return;
    const track = carousel.querySelector('[data-te-product-track]');
    const cards = [...carousel.querySelectorAll('[data-te-offer-product]')];
    if (!track || cards.length === 0) return;
    const safeIndex = Math.max(0, Math.min(cards.length - 1, index));
    carousel.dataset.activeProductIndex = String(safeIndex);
    cards.forEach((card, cardIndex) => card.classList.toggle('is-active', cardIndex === safeIndex));
    carousel.querySelectorAll('[data-te-product-dot]').forEach((dot, dotIndex) => {
      const active = dotIndex === safeIndex;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    const previous = carousel.querySelector('[data-te-product-prev]');
    const next = carousel.querySelector('[data-te-product-next]');
    if (previous) previous.disabled = safeIndex <= 0;
    if (next) next.disabled = safeIndex >= cards.length - 1;
    if (!shouldScroll && Math.abs(track.scrollLeft - cards[safeIndex].offsetLeft) > 2) {
      track.scrollTo({ left: cards[safeIndex].offsetLeft, behavior: 'auto' });
    }
    if (shouldScroll) {
      track.scrollTo({ left: cards[safeIndex].offsetLeft, behavior: this.reducedMotion() ? 'auto' : 'smooth' });
    }
  }

  moveProduct(control, direction) {
    const carousel = control.closest('[data-te-product-carousel]');
    const current = Number(carousel.dataset.activeProductIndex || 0);
    this.setActiveProduct(carousel, current + direction, true);
  }

  syncProductFromScroll(carousel) {
    const track = carousel.querySelector('[data-te-product-track]');
    const cards = [...carousel.querySelectorAll('[data-te-offer-product]')];
    if (!track || cards.length < 2) return;
    const closest = cards.reduce((best, card, index) => {
      const distance = Math.abs(card.offsetLeft - track.scrollLeft);
      return distance < best.distance ? { index, distance } : best;
    }, { index: 0, distance: Number.POSITIVE_INFINITY });
    this.setActiveProduct(carousel, closest.index, false);
  }

  initializeOfferProduct(card) {
    if (!card || card.dataset.teVariantInitialized === 'true') return;
    card.dataset.teVariantInitialized = 'true';
    card.teVariants = this.parseOfferVariants(card);
    const allowedVariants = card.teVariants.filter((variant) => variant.allowed);
    if (allowedVariants.length === 0) return;

    const selects = [...card.querySelectorAll('[data-te-offer-option-select]')];
    card.querySelectorAll('[data-te-offer-dropdown]').forEach((dropdown) => this.initializeOfferDropdown(dropdown));
    const referenceOptions = this.parseOfferReferenceOptions(card);
    const preferredValueFor = (select) => {
      const optionName = (select.dataset.optionName || '').trim().toLowerCase();
      return referenceOptions.find((option) =>
        String(option.name || '').trim().toLowerCase() === optionName
      )?.value || '';
    };
    const colorSelect = selects.find((select) => select.dataset.optionType === 'color');
    if (colorSelect) {
      const optionIndex = Number(colorSelect.dataset.optionIndex) - 1;
      const preferred = String(preferredValueFor(colorSelect)).toLowerCase();
      const options = [...colorSelect.options].filter((option) => option.value);
      const selected = options.find((option) =>
        option.value.toLowerCase() === preferred &&
        allowedVariants.some((variant) => variant.options[optionIndex] === option.value)
      ) || options.find((option) =>
        allowedVariants.some((variant) => variant.options[optionIndex] === option.value)
      );
      if (selected) colorSelect.value = selected.value;
    }

    const sizeSelect = selects.find((select) => select.dataset.optionType === 'size');
    if (sizeSelect) {
      const optionIndex = Number(sizeSelect.dataset.optionIndex) - 1;
      const preferred = String(preferredValueFor(sizeSelect)).toLowerCase();
      const selections = this.getOfferSelections(card);
      const selected = [...sizeSelect.options].find((option) =>
        option.value &&
        option.value.toLowerCase() === preferred &&
        allowedVariants.some((variant) =>
          this.variantMatchesSelections(variant, selections, optionIndex, option.value)
        )
      );
      if (selected) sizeSelect.value = selected.value;
    }

    selects.filter((select) => !['color', 'size'].includes(select.dataset.optionType)).forEach((select) => {
      const optionIndex = Number(select.dataset.optionIndex) - 1;
      const preferred = String(preferredValueFor(select)).toLowerCase();
      const selections = this.getOfferSelections(card);
      const selected = [...select.options].find((option) =>
        option.value &&
        option.value.toLowerCase() === preferred &&
        allowedVariants.some((variant) =>
          this.variantMatchesSelections(variant, selections, optionIndex, option.value)
        )
      );
      if (selected) select.value = selected.value;
      else if (select.options.length === 2 && allowedVariants.some((variant) => variant.options[optionIndex] === select.options[1].value)) {
        select.value = select.options[1].value;
      }
    });

    this.updateOfferProduct(card);
  }

  initializeOfferDropdown(dropdown) {
    if (!dropdown || dropdown.dataset.teDropdownInitialized === 'true') return;
    dropdown.dataset.teDropdownInitialized = 'true';

    const summary = dropdown.querySelector('summary');
    const panel = dropdown.querySelector('[data-te-offer-dropdown-panel]');
    if (!summary || !panel) return;

    dropdown.addEventListener('toggle', () => {
      summary.setAttribute('aria-expanded', dropdown.open ? 'true' : 'false');
      if (!dropdown.open) {
        if (typeof panel.hidePopover === 'function' && panel.matches(':popover-open')) panel.hidePopover();
        panel.classList.remove('is-open');
        return;
      }

      this.closeOfferDropdowns(dropdown);
      if (typeof panel.showPopover === 'function') {
        if (!panel.matches(':popover-open')) panel.showPopover();
      } else {
        panel.classList.add('is-open');
      }
      this.positionOfferDropdown(dropdown);
    });

    panel.addEventListener('toggle', (event) => {
      if (event.newState === 'closed' && dropdown.open) dropdown.removeAttribute('open');
    });

    dropdown.addEventListener('keydown', (event) => this.onOfferDropdownKeydown(event, dropdown));
  }

  onOfferDropdownKeydown(event, dropdown) {
    const panel = dropdown.querySelector('[data-te-offer-dropdown-panel]');
    const options = [...panel.querySelectorAll('[data-te-offer-option-value]:not(:disabled)')];
    const currentIndex = options.indexOf(document.activeElement);

    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (event.target.closest('summary') && ['ArrowDown', 'ArrowUp'].includes(event.key)) {
      event.preventDefault();
      if (!dropdown.open) dropdown.setAttribute('open', '');
      window.setTimeout(() => {
        (event.key === 'ArrowDown' ? options[0] : options[options.length - 1])?.focus();
      }, 0);
      return;
    }

    if (currentIndex < 0) return;
    let nextIndex = currentIndex;
    if (event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % options.length;
    else if (event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + options.length) % options.length;
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = options.length - 1;
    else return;

    event.preventDefault();
    options[nextIndex]?.focus();
  }

  positionOfferDropdown(dropdown) {
    const summary = dropdown.querySelector('summary');
    const panel = dropdown.querySelector('[data-te-offer-dropdown-panel]');
    const drawer = this.querySelector('.drawer__inner');
    if (!summary || !panel || !drawer) return;

    const summaryRect = summary.getBoundingClientRect();
    const drawerRect = drawer.getBoundingClientRect();
    const width = Math.max(summaryRect.width, 128);
    const left = Math.min(Math.max(summaryRect.left, drawerRect.left + 8), drawerRect.right - width - 8);
    panel.style.setProperty('--te-option-panel-left', `${left}px`);
    panel.style.setProperty('--te-option-panel-width', `${width}px`);
    panel.style.setProperty('--te-option-panel-top', `${summaryRect.bottom + 6}px`);

    requestAnimationFrame(() => {
      const panelHeight = panel.getBoundingClientRect().height;
      if (summaryRect.bottom + panelHeight + 14 > drawerRect.bottom) {
        panel.style.setProperty('--te-option-panel-top', `${Math.max(drawerRect.top + 8, summaryRect.top - panelHeight - 6)}px`);
      }
    });
  }

  closeOfferDropdown(dropdown, restoreFocus = false) {
    if (!dropdown) return;
    const summary = dropdown.querySelector('summary');
    const panel = dropdown.querySelector('[data-te-offer-dropdown-panel]');
    dropdown.removeAttribute('open');
    summary?.setAttribute('aria-expanded', 'false');
    if (panel) {
      if (typeof panel.hidePopover === 'function' && panel.matches(':popover-open')) panel.hidePopover();
      panel.classList.remove('is-open');
    }
    if (restoreFocus) summary?.focus();
  }

  closeOfferDropdowns(exception = null) {
    this.querySelectorAll('[data-te-offer-dropdown][open]').forEach((dropdown) => {
      if (dropdown !== exception) this.closeOfferDropdown(dropdown);
    });
  }

  syncOfferDropdown(select) {
    const control = select.closest('[data-te-offer-option-control]');
    if (!control) return;

    const selectedOption = select.selectedOptions[0];
    const selectedValue = selectedOption?.value || '';
    const label = control.querySelector('[data-te-offer-dropdown-label]');
    const summary = control.querySelector('summary');
    if (label) label.textContent = selectedOption?.textContent?.trim() || select.options[0]?.textContent?.trim() || '';
    summary?.classList.toggle('has-value', Boolean(selectedValue));

    control.querySelectorAll('[data-te-offer-option-value]').forEach((button) => {
      const nativeOption = [...select.options].find((option) => option.value === button.dataset.teOfferOptionValue);
      const isSelected = Boolean(selectedValue) && button.dataset.teOfferOptionValue === selectedValue;
      const isDisabled = !nativeOption || nativeOption.disabled;
      button.disabled = isDisabled;
      button.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');
      button.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      button.classList.toggle('is-selected', isSelected);
    });
  }

  parseOfferVariants(card) {
    try {
      return JSON.parse(card.querySelector('[data-te-offer-variants]')?.textContent || '[]');
    } catch (error) {
      console.error('Could not read cart offer variants.', error);
      return [];
    }
  }

  parseOfferReferenceOptions(card) {
    try {
      const parsed = JSON.parse(card.dataset.referenceOptions || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error('Could not read cart offer reference options.', error);
      return [];
    }
  }

  getOfferSelections(card) {
    const selections = [];
    card.querySelectorAll('[data-te-offer-option-select]').forEach((select) => {
      selections[Number(select.dataset.optionIndex) - 1] = select.value || null;
    });
    return selections;
  }

  variantMatchesSelections(variant, selections, ignoredIndex = -1, candidateValue = null) {
    return selections.every((selected, index) => {
      const expected = index === ignoredIndex ? candidateValue : selected;
      return !expected || variant.options[index] === expected;
    });
  }

  updateOfferProduct(card) {
    if (!card) return;
    const variants = card.teVariants || this.parseOfferVariants(card);
    const allowedVariants = variants.filter((variant) => variant.allowed);
    let selections = this.getOfferSelections(card);
    const colorIndex = Number(card.dataset.colorPosition || 0) - 1;
    const sizeIndex = Number(card.dataset.sizePosition || 0) - 1;

    const colorSelect = card.querySelector('[data-te-offer-option-select][data-option-type="color"]');
    if (colorSelect) {
      const colorSelections = [...selections];
      if (sizeIndex >= 0) colorSelections[sizeIndex] = null;
      [...colorSelect.options].forEach((option) => {
        if (!option.value) return;
        option.disabled = !allowedVariants.some((variant) =>
          this.variantMatchesSelections(variant, colorSelections, colorIndex, option.value)
        );
      });
      if (colorSelect.selectedOptions[0]?.disabled) colorSelect.value = '';
    }

    selections = this.getOfferSelections(card);

    card.querySelectorAll('[data-te-offer-option-select]').forEach((select) => {
      if (select === colorSelect) return;
      const optionIndex = Number(select.dataset.optionIndex) - 1;
      [...select.options].forEach((option) => {
        if (!option.value) return;
        option.disabled = !allowedVariants.some((variant) =>
          this.variantMatchesSelections(variant, selections, optionIndex, option.value)
        );
      });
      if (select.selectedOptions[0]?.disabled) select.value = '';
    });

    selections = this.getOfferSelections(card);
    card.querySelectorAll('[data-te-offer-option-select]').forEach((select) => {
      select.classList.toggle('has-value', Boolean(select.value));
      this.syncOfferDropdown(select);
    });

    const controlCount = card.querySelectorAll('[data-te-offer-option-select]').length;
    const selectedCount = selections.filter(Boolean).length;
    const selectedVariant = controlCount === selectedCount
      ? allowedVariants.find((variant) => this.variantMatchesSelections(variant, selections))
      : null;
    const previewVariant = selectedVariant || allowedVariants.find((variant) => this.variantMatchesSelections(variant, selections)) || allowedVariants[0];

    const image = card.querySelector('[data-te-offer-product-image]');
    if (image && previewVariant?.image) image.src = previewVariant.image;

    const price = card.querySelector('[data-te-offer-price]');
    const comparePrice = card.querySelector('[data-te-offer-compare-price]');
    const effectivePrice = card.dataset.effectivePrice;
    const showCompare = card.dataset.showComparePrice === 'true';
    if (price && previewVariant) price.textContent = effectivePrice || previewVariant.price;
    if (comparePrice && previewVariant) {
      const comparison = effectivePrice ? previewVariant.price : previewVariant.comparePrice;
      comparePrice.textContent = comparison || '';
      comparePrice.hidden = !showCompare || !comparison || comparison === previewVariant.price && !effectivePrice;
    }

    const addButton = card.querySelector('[data-te-cart-upsell-add]');
    if (!addButton) return;
    if (selectedVariant) {
      addButton.dataset.variantId = selectedVariant.id;
      addButton.disabled = false;
      addButton.setAttribute('aria-disabled', 'false');
      addButton.textContent = card.dataset.buttonLabel || 'ADD';
    } else {
      addButton.dataset.variantId = '';
      addButton.disabled = true;
      addButton.setAttribute('aria-disabled', 'true');
      addButton.textContent = card.dataset.buttonLabel || 'ADD';
    }
  }

  reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    const previousShippingProgress = this.getShippingProgress();
    const previousOfferId = this.getActiveOfferId();
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      if (section.id === 'cart-drawer') {
        this.animateShippingProgress(previousShippingProgress);
        this.initializeOfferBuilder(previousOfferId);
      }
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  refreshContents(parsedState, preferredOfferId = this.getActiveOfferId()) {
    this.productId = parsedState.id;
    const previousShippingProgress = this.getShippingProgress();
    const drawerHTML = parsedState.sections?.['cart-drawer'];
    if (drawerHTML) {
      const parsed = new DOMParser().parseFromString(drawerHTML, 'text/html');
      const source = parsed.querySelector('#CartDrawer');
      const target = document.getElementById('CartDrawer');
      if (source && target) {
        target.innerHTML = source.innerHTML;
        this.animateShippingProgress(previousShippingProgress);
        this.initializeOfferBuilder(preferredOfferId);
      }
    }

    const iconHTML = parsedState.sections?.['cart-icon-bubble'];
    if (iconHTML) {
      const parsed = new DOMParser().parseFromString(iconHTML, 'text/html');
      const source = parsed.querySelector('.shopify-section');
      const target = document.getElementById('cart-icon-bubble');
      if (source && target) {
        target.innerHTML = source.innerHTML;
      }
    }

    this.classList.toggle('is-empty', parsedState.item_count === 0);
    this.classList.add('animate', 'active');
    document.body.classList.add('overflow-hidden');
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
  }

  getShippingProgress() {
    const shipping = this.querySelector('.te-cart-shipping');
    if (!shipping) return null;

    const progress = parseFloat(shipping.style.getPropertyValue('--te-shipping-progress'));
    if (!Number.isNaN(progress)) return progress;

    const bar = shipping.querySelector('.te-cart-shipping__bar');
    const track = shipping.querySelector('.te-cart-shipping__track');
    if (!bar || !track || !track.clientWidth) return null;

    return (bar.clientWidth / track.clientWidth) * 100;
  }

  animateShippingProgress(previousProgress) {
    if (previousProgress === null || previousProgress === undefined) return;

    const shipping = this.querySelector('.te-cart-shipping');
    const bar = shipping?.querySelector('.te-cart-shipping__bar');
    if (!shipping || !bar) return;

    const nextProgress = parseFloat(shipping.style.getPropertyValue('--te-shipping-progress'));
    if (Number.isNaN(nextProgress)) return;

    const from = Math.max(0, Math.min(100, previousProgress));
    const to = Math.max(0, Math.min(100, nextProgress));

    shipping.style.setProperty('--te-shipping-progress', `${from}%`);
    bar.style.setProperty('width', `${from}%`, 'important');
    bar.style.setProperty('transition', 'none', 'important');
    bar.getBoundingClientRect();

    requestAnimationFrame(() => {
      bar.style.removeProperty('transition');
      bar.style.removeProperty('width');
      shipping.style.setProperty('--te-shipping-progress', `${to}%`);
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
